/* eslint-disable react-hooks/exhaustive-deps */
import { InfiniteData, useQueryClient } from "@tanstack/react-query";
import { useRefetchQuery } from "libs/reactQuery";
import { SocketsContext } from "libs/soket";
import { useAiTyping } from "modules/aiConversations";
import { useSupportConversationsCounter } from "modules/common";
import { useContext, useEffect } from "react";

import { InAppMessageSender, SUPPORT_QUERY_KEY } from "../constants/enums";
import { insertOrReplaceMessage } from "../helpers";
import {
  InAppConversation,
  InAppConversationMessagesResponse,
  InAppConversationsListResponse,
  InAppMessage
} from "../support.types";

type ConversationsInfinite = InfiniteData<InAppConversationsListResponse>;
type MessagesInfinite = InfiniteData<InAppConversationMessagesResponse>;

export const useSupportRealTimeUpdate = () => {
  const { inAppSupportAdminSocket } = useContext(SocketsContext);
  const queryClient = useQueryClient();
  const refetchConversations = useRefetchQuery([SUPPORT_QUERY_KEY, "list"]);
  const { openConversationIdRef } = useSupportConversationsCounter();
  const { addTyping, removeTyping } = useAiTyping();

  useEffect(() => {
    const socket = inAppSupportAdminSocket.socket;
    if (!socket) return;

    const patchListWith = (updated: InAppConversation) => {
      queryClient.setQueriesData<ConversationsInfinite>(
        { queryKey: [SUPPORT_QUERY_KEY, "list"] },
        (current) => {
          if (!current) return current;
          let touched = false;
          const pages = current.pages.map((page) => {
            if (!page.data.some((c) => c.id === updated.id)) return page;
            touched = true;
            return {
              ...page,
              data: page.data.map((c) =>
                c.id === updated.id
                  ? {
                      ...c,
                      ...updated,
                      // WS payload doesn't carry these — preserve from cache
                      unreadCount: c.unreadCount,
                      reseller: c.reseller ?? updated.reseller ?? null,
                      lastMessage: c.lastMessage ?? updated.lastMessage ?? null
                    }
                  : c
              )
            };
          });
          if (!touched) return current;
          return { ...current, pages };
        }
      );
    };

    const onConversationCreated = (_data: InAppConversation) => {
      refetchConversations();
    };

    const onConversationUpdated = (data: InAppConversation) => {
      patchListWith(data);
    };

    const onAiGeneratingStarted = (data: { conversationId: string }) => {
      addTyping(data.conversationId);
    };

    const onMessageCreated = (message: InAppMessage) => {
      // No `ai.generating.ended` event — clear the indicator when the AI's
      // reply actually lands (the provider also has a safety timeout).
      if (message.sender === InAppMessageSender.Ai) {
        removeTyping(message.conversationId);
      }

      queryClient.setQueriesData<ConversationsInfinite>(
        { queryKey: [SUPPORT_QUERY_KEY, "list"] },
        (current) => {
          if (!current) return current;
          return {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              data: page.data.map((c) => {
                if (c.id !== message.conversationId) return c;
                const isInbound = message.direction === "inbound";
                const isOpenedByMe =
                  openConversationIdRef.current === message.conversationId;
                return {
                  ...c,
                  lastMessage: message,
                  lastInboundAt: isInbound
                    ? message.createdAt
                    : c.lastInboundAt,
                  unreadCount:
                    isInbound && !isOpenedByMe
                      ? (c.unreadCount ?? 0) + 1
                      : c.unreadCount
                };
              })
            }))
          };
        }
      );

      queryClient.setQueriesData<MessagesInfinite>(
        {
          queryKey: [SUPPORT_QUERY_KEY, "messages", message.conversationId]
        },
        (current) => insertOrReplaceMessage(current, message)
      );
    };

    socket.on("in-app-conversation.created", onConversationCreated);
    socket.on("in-app-conversation.updated", onConversationUpdated);
    socket.on("in-app-message.created", onMessageCreated);
    socket.on("ai.generating.started", onAiGeneratingStarted);

    return () => {
      socket.off("in-app-conversation.created", onConversationCreated);
      socket.off("in-app-conversation.updated", onConversationUpdated);
      socket.off("in-app-message.created", onMessageCreated);
      socket.off("ai.generating.started", onAiGeneratingStarted);
    };
  }, [inAppSupportAdminSocket.socket]);
};
